import { Injectable, signal } from '@angular/core';

/**
 * Global card_required 402 handler (owner ruling 2026-08-18): any write
 * against a live project — activation, order submit, EPO/warranty
 * create+submit, task-work writes, section/price edits, everything — is
 * refused with 402 card_required when the org has no payment method on
 * file. See billing.hardlock.CardRequiredForLiveProjectWrites (backend)
 * and auth-interceptor.ts (the ONE place that catches this 402, for every
 * call site except the start-project modal, which already had its own
 * inline card step before this and keeps it — see auth-interceptor.ts's
 * isStartProjectCall exemption).
 *
 * A billing admin sees this add-card modal, adds a card, and the
 * interceptor retries the original request once, transparently. Anyone
 * else (most commonly a subcontractor recording their own work) can't add
 * the org's card — the interceptor shows them a toast instead of this
 * modal (see auth-interceptor.ts). This service only ever drives the
 * modal path.
 */
@Injectable({ providedIn: 'root' })
export class CardRequiredService {
  readonly visible = signal(false);
  readonly trialEndsAt = signal<string | null>(null);

  private resolveFn: ((cardAdded: boolean) => void) | null = null;

  /** Opens the modal and resolves once the user either adds a card
   * (true) or cancels (false). Only one prompt is ever open at a time —
   * a second call while one is pending resolves the first as canceled. */
  promptForCard(trialEndsAt: string | null): Promise<boolean> {
    if (this.resolveFn) {
      this.resolveFn(false);
    }
    this.trialEndsAt.set(trialEndsAt);
    this.visible.set(true);
    return new Promise<boolean>(resolve => {
      this.resolveFn = resolve;
    });
  }

  cardAdded(): void {
    this.visible.set(false);
    this.resolveFn?.(true);
    this.resolveFn = null;
  }

  cancel(): void {
    this.visible.set(false);
    this.resolveFn?.(false);
    this.resolveFn = null;
  }
}
